import { services } from '@config/services';
import { vendorApi } from './_base';

/**
 * Vendor config envelope endpoints
 * (`/v1/vendors/{vendorId}/config*`). Only consumer today is the FTP
 * Inventory Sync settings — other config sections exist on the wire
 * but aren't surfaced in portal2.
 */

/**
 * FTP inventory-sync settings stored on the vendor config envelope. Shape
 * is verbatim from the legacy service — keep field names exactly as the
 * backend expects them.
 */
export interface VendorFtpInventorySettings {
  FtpEnabled?: boolean;
  FtpHost?: string;
  FtpPort?: number;
  FtpProtocol?: 'FTP' | 'SFTP';
  FtpUsername?: string;
  FtpPassword?: string;
  RunIntervalPerHour?: number;
  FtpInventoryFileDirectory?: string;
  FtpInventoryFileName?: string;
  FtpInventoryControlFileName?: string;
}

export interface VendorConfigData {
  FTPInventorySettings?: VendorFtpInventorySettings;
  /** Other config sections exist; only FTP is consumed by the port today. */
  [key: string]: unknown;
}

/**
 * Config envelope returned by `GET /v1/vendors/{vendorId}/config`. The
 * legacy response is double-wrapped (`{ data: { data: {...} } }`) — kept
 * verbatim so the wire contract isn't reinterpreted; the transform
 * unwraps once so consumers read `data` directly.
 */
export interface VendorConfigEnvelope {
  ownerId?: string;
  schemaName?: string;
  /**
   * Version of the STORED config row, which lags the latest schema whenever a
   * new one has been seeded since this vendor last saved. Never send this on a
   * write — see `mergedWithSchemaVersion`.
   */
  schemaVersion?: string | number;
  /**
   * Latest schema version the returned data was merged against. Config MS
   * rejects any write whose `schemaVersion` is not the latest, so this is the
   * value a PUT must carry. Absent only if the service stops returning it.
   */
  mergedWithSchemaVersion?: string | number;
  data: VendorConfigData;
}

const vendorConfigApi = vendorApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * GET /config/v1/config?ownerId={vendorId}&schemaName=VendorConfiguration
     * — vendor config envelope (FTP sync settings) from the Config
     * microservice, the same store the legacy portal reads
     * (`ConfigServices.getConfiguration(vendorId, 'VendorConfiguration')`)
     * and the same pattern as the merchant config envelope. The vendor
     * service has no `/vendors/{id}/config` route, so the previous URL 404'd
     * and every populated field rendered "-".
     *
     * Wire-shape lock — live response (schemaVersion 0.0.7):
     *   { ownerId, schemaName: 'VendorConfiguration', schemaVersion,
     *     data: { FTPInventorySettings: { FtpEnabled, FtpHost, FtpPort,
     *       FtpProtocol, FtpUsername, FtpPassword, RunIntervalPerHour,
     *       FtpInventoryFileDirectory, FtpInventoryFileName,
     *       FtpInventoryControlFileName } } }
     * Returned verbatim (no unwrap) so consumers read `envelope.data`.
     */
    getVendorConfig: builder.query<VendorConfigEnvelope, { vendorId: string }>({
      query: ({ vendorId }) => ({
        url: `${services.config}/v1/config?ownerId=${encodeURIComponent(vendorId)}&schemaName=VendorConfiguration`,
        method: 'GET',
      }),
      providesTags: (_r, _e, { vendorId }) => [{ type: 'VendorConfig', id: vendorId }],
    }),
    /**
     * PUT /config/v1/config — replace the vendor config envelope on the
     * Config microservice (mirrors legacy `updateConfiguration` and the
     * merchant config PUT). Body is the full envelope; `schemaVersion` must be
     * the LATEST schema version (`mergedWithSchemaVersion` off the GET), not
     * the stored row's — Config MS validates it and 400s otherwise, which
     * silently breaks saves for every vendor whose row predates a schema bump.
     */
    updateVendorConfig: builder.mutation<
      VendorConfigEnvelope,
      { vendorId: string; schemaVersion?: string | number; data: VendorConfigData }
    >({
      query: ({ vendorId, schemaVersion, data }) => ({
        url: `${services.config}/v1/config`,
        method: 'PUT',
        body: {
          ownerId: vendorId,
          schemaName: 'VendorConfiguration',
          schemaVersion,
          data,
        },
      }),
      invalidatesTags: (_r, _e, { vendorId }) => [{ type: 'VendorConfig', id: vendorId }],
    }),
  }),
});

export const {
  useGetVendorConfigQuery,
  useUpdateVendorConfigMutation,
} = vendorConfigApi;
